import { Component, signal, computed, ChangeDetectionStrategy } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

type Periode = '7j' | '30j' | '3m';
type Trend = 'up' | 'down' | 'stable';

interface SiteStats {
  id: string;
  nom: string;
  note: number;
  avis: number;
  trend: Trend;
  trendVal: number;
  sparkline: number[];
  repartition: [number, number, number, number, number];
  categories: { accueil: number; attente: number; prelevement: number; resultats: number };
}

@Component({
  selector: 'app-satisfaction-screen',
  standalone: true,
  imports: [TranslatePipe],
  templateUrl: './satisfaction-screen.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./satisfaction-screen.component.scss'],
})
export class SatisfactionScreenComponent {
  readonly periode = signal<Periode>('30j');

  readonly periodes: { key: Periode; label: string }[] = [
    { key: '7j',  label: '7 jours' },
    { key: '30j', label: '30 jours' },
    { key: '3m',  label: '3 mois' },
  ];

  private readonly dataMap: Record<Periode, SiteStats[]> = {
    '7j': [
      { id: 'nantes-centre',  nom: 'Nantes Centre',  note: 3.1, avis: 22, trend: 'down',   trendVal: -0.4, sparkline: [3.8, 3.5, 3.4, 3.2, 3.0, 3.1], repartition: [18, 18, 23, 27, 14], categories: { accueil: 3.4, attente: 2.6, prelevement: 3.5, resultats: 3.0 } },
      { id: 'carquefou',      nom: 'Carquefou',      note: 3.9, avis: 14, trend: 'stable', trendVal:  0.0, sparkline: [3.8, 4.0, 3.9, 3.7, 4.0, 3.9], repartition: [29, 36, 21, 7, 7],   categories: { accueil: 4.2, attente: 3.6, prelevement: 4.0, resultats: 3.8 } },
      { id: 'saint-herblain', nom: 'Saint-Herblain', note: 3.6, avis: 18, trend: 'down',   trendVal: -0.2, sparkline: [4.0, 3.9, 3.7, 3.8, 3.6, 3.6], repartition: [22, 33, 22, 17, 6],  categories: { accueil: 3.9, attente: 3.2, prelevement: 3.8, resultats: 3.4 } },
      { id: 'reze',           nom: 'Rezé',           note: 4.6, avis: 11, trend: 'up',     trendVal: +0.3, sparkline: [4.2, 4.3, 4.4, 4.5, 4.4, 4.6], repartition: [64, 27, 9, 0, 0],    categories: { accueil: 4.8, attente: 4.3, prelevement: 4.7, resultats: 4.6 } },
      { id: 'orvault',        nom: 'Orvault',        note: 4.2, avis: 9,  trend: 'up',     trendVal: +0.1, sparkline: [4.0, 4.1, 4.0, 4.1, 4.2, 4.2], repartition: [44, 44, 12, 0, 0],   categories: { accueil: 4.4, attente: 3.9, prelevement: 4.3, resultats: 4.1 } },
      { id: 'vertou',         nom: 'Vertou',         note: 3.4, avis: 7,  trend: 'stable', trendVal:  0.0, sparkline: [3.3, 3.5, 3.4, 3.3, 3.5, 3.4], repartition: [14, 29, 29, 14, 14], categories: { accueil: 3.6, attente: 3.1, prelevement: 3.5, resultats: 3.3 } },
    ],
    '30j': [
      { id: 'nantes-centre',  nom: 'Nantes Centre',  note: 3.3, avis: 87, trend: 'down',   trendVal: -0.3, sparkline: [3.8, 3.6, 3.5, 3.4, 3.3, 3.3], repartition: [25, 25, 13, 25, 13], categories: { accueil: 3.6, attente: 2.8, prelevement: 3.7, resultats: 3.1 } },
      { id: 'carquefou',      nom: 'Carquefou',      note: 4.0, avis: 56, trend: 'stable', trendVal: +0.1, sparkline: [3.9, 4.0, 3.8, 4.1, 4.0, 4.0], repartition: [36, 36, 18, 5, 5],   categories: { accueil: 4.3, attente: 3.7, prelevement: 4.1, resultats: 3.9 } },
      { id: 'saint-herblain', nom: 'Saint-Herblain', note: 3.8, avis: 72, trend: 'up',     trendVal: +0.2, sparkline: [3.5, 3.6, 3.7, 3.8, 3.7, 3.8], repartition: [28, 39, 17, 11, 5],  categories: { accueil: 4.0, attente: 3.4, prelevement: 4.0, resultats: 3.7 } },
      { id: 'reze',           nom: 'Rezé',           note: 4.5, avis: 43, trend: 'up',     trendVal: +0.2, sparkline: [4.2, 4.3, 4.3, 4.4, 4.5, 4.5], repartition: [60, 30, 7, 2, 0],    categories: { accueil: 4.7, attente: 4.2, prelevement: 4.6, resultats: 4.4 } },
      { id: 'orvault',        nom: 'Orvault',        note: 4.1, avis: 38, trend: 'stable', trendVal:  0.0, sparkline: [4.0, 4.1, 4.2, 4.0, 4.1, 4.1], repartition: [42, 45, 8, 5, 0],    categories: { accueil: 4.3, attente: 3.8, prelevement: 4.2, resultats: 4.0 } },
      { id: 'vertou',         nom: 'Vertou',         note: 3.5, avis: 29, trend: 'down',   trendVal: -0.1, sparkline: [3.7, 3.6, 3.5, 3.6, 3.4, 3.5], repartition: [17, 34, 28, 10, 10],  categories: { accueil: 3.7, attente: 3.2, prelevement: 3.6, resultats: 3.4 } },
    ],
    '3m': [
      { id: 'nantes-centre',  nom: 'Nantes Centre',  note: 3.5, avis: 264, trend: 'down',   trendVal: -0.4, sparkline: [4.0, 3.8, 3.7, 3.6, 3.4, 3.5], repartition: [22, 28, 17, 22, 11], categories: { accueil: 3.8, attente: 3.0, prelevement: 3.8, resultats: 3.4 } },
      { id: 'carquefou',      nom: 'Carquefou',      note: 3.9, avis: 167, trend: 'stable', trendVal: +0.0, sparkline: [3.8, 3.9, 4.0, 3.9, 3.8, 3.9], repartition: [32, 36, 20, 7, 5],   categories: { accueil: 4.1, attente: 3.6, prelevement: 4.0, resultats: 3.8 } },
      { id: 'saint-herblain', nom: 'Saint-Herblain', note: 3.7, avis: 211, trend: 'up',     trendVal: +0.3, sparkline: [3.3, 3.4, 3.5, 3.6, 3.7, 3.7], repartition: [25, 38, 18, 13, 5],  categories: { accueil: 3.9, attente: 3.3, prelevement: 3.9, resultats: 3.6 } },
      { id: 'reze',           nom: 'Rezé',           note: 4.4, avis: 129, trend: 'up',     trendVal: +0.3, sparkline: [4.0, 4.1, 4.2, 4.3, 4.4, 4.4], repartition: [56, 33, 8, 2, 0],    categories: { accueil: 4.6, attente: 4.1, prelevement: 4.5, resultats: 4.3 } },
      { id: 'orvault',        nom: 'Orvault',        note: 4.0, avis: 112, trend: 'stable', trendVal: +0.1, sparkline: [3.9, 4.0, 3.9, 4.0, 4.1, 4.0], repartition: [40, 44, 10, 4, 1],   categories: { accueil: 4.2, attente: 3.7, prelevement: 4.1, resultats: 3.9 } },
      { id: 'vertou',         nom: 'Vertou',         note: 3.6, avis: 88, trend: 'up',      trendVal: +0.2, sparkline: [3.3, 3.4, 3.4, 3.5, 3.6, 3.6], repartition: [20, 34, 26, 11, 9],  categories: { accueil: 3.8, attente: 3.3, prelevement: 3.7, resultats: 3.5 } },
    ],
  };

  readonly sites = computed(() => this.dataMap[this.periode()]);

  readonly globalNote = computed(() => {
    const s = this.sites();
    const totalAvis = s.reduce((a, b) => a + b.avis, 0);
    return (s.reduce((a, b) => a + b.note * b.avis, 0) / totalAvis).toFixed(1);
  });

  readonly totalAvis = computed(() => this.sites().reduce((a, b) => a + b.avis, 0));

  readonly globalRepartition = computed(() => {
    const s = this.sites();
    const total = s.reduce((a, b) => a + b.avis, 0);
    const sums = [0, 0, 0, 0, 0];
    s.forEach(site => site.repartition.forEach((pct, i) => { sums[i] += pct * site.avis / 100; }));
    return sums.map(v => Math.round(v / total * 100));
  });

  noteColor(n: number): string {
    if (n >= 4) return '#059669';
    if (n >= 3) return '#D97706';
    return '#DC2626';
  }

  noteBg(n: number): string {
    if (n >= 4) return '#D1FAE5';
    if (n >= 3) return '#FEF3C7';
    return '#FEE2E2';
  }

  catAvg(key: string): number {
    const s = this.sites();
    const total = s.reduce((a, b) => a + b.avis, 0);
    const sum = s.reduce((a, b) => a + (b.categories as any)[key] * b.avis, 0);
    return +(sum / total).toFixed(2);
  }

  sparklinePath(pts: number[]): string {
    const w = 52, h = 24, min = 1, max = 5;
    const xs = pts.map((_, i) => i / (pts.length - 1) * w);
    const ys = pts.map(v => h - ((v - min) / (max - min)) * h);
    return xs.map((x, i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${ys[i].toFixed(1)}`).join(' ');
  }
}